import { useEffect, useMemo, useState } from "react";
import usePersistentFilters, { usePersistentState } from "../Common/usePersistentFilters";
import { Badge, Button, Card, Col, Dropdown, Form, Modal, Row, Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import swal from "sweetalert";
import api, { getFileUrl } from "../../../api";
import CanAccess from "../../../routes/CanAccess";
import PageTitle from "../../layouts/PageTitle";
import AppPagination from "../Common/AppPagination";

/* =============================
 ROUTE AVAILABILITY MAP
 (single source of truth)
============================= */
const ROUTE_LOOKUP = {
 DPR: "dpr",
 WIR: "wir",
 MIR: "mir",
 "JMR Summary": "jmr",
 "TC/Warranty Card": "tc-documents",
 Drawing: "documents",
 "TDS/Manual": "tds",
 PO: "purchase-orders",
 "JMR Sheets": "measurement-sheets",
 GC: "work-orders/:id/gantt",
 BOQA: "boq/amendment/list",
 HIRA: "documents",
 MOS: "documents",
 // "+ Custom": "documents",
 MOM: "mom/list",
 WO: "work-orders",
 Indent: "indent",
 DC: "delivery",
 "Contractor DC": "contractor-delivery",
 "Testing & Commissioning": "tc-report",
 "T & C Report": "tc-report",
 "Handover Documents": "handover",
 "Contractor WO": "contractual-debit",
 "Contractor Billing": "contractor-billing",
 "Vendor Billing": "vendor-work-order-billing",
 Reconciliation: "reconciliation",
 Documents: "documents",
 Billings: "billings",
};

/* =============================
 BACKEND API MAP (FIX)
============================= */
const API_LOOKUP = {
 DPR: "/dpr",
 WIR: "/wir",
 MIR: "/mir",
 "JMR Summary": "/jmr",
 "JMR Sheets": "/measurement-sheets",
 "TC/Warranty Card": (id) => `/document-control?workOrder=${id}&type=TC&limit=1`,
 Drawing: "/documents",
 "TDS/Manual": (id) => `/document-control?workOrder=${id}&type=TDS&limit=1`,
 PO: "/purchase-orders",
 MOM: "/mom/list",
 BOQA: "/boq-amendments",
 GC: (id) => `/gantt-chart/${id}`,
 HIRA: "/documents",
 MOS: "/documents",
 // "+ Custom": "/documents",
 WO: "/work-orders",
 Indent: "/indents",
 DC: "/deliveries",
 "Contractor DC": "/contractor-delivery",
 "Testing & Commissioning": "/testing-commissioning",
 "T & C Report": "/testing-commissioning",
 "Handover Documents": "/handover",
 "Contractor WO": "/contractual-work-orders",
 "Contractor Billing": "/contractor-billings",
 "Vendor Billing": "/vendor-work-order-billings",
 Reconciliation: (id) => `/reconciliation/mrs?workOrderId=${id}&limit=1`,
 Documents: "/documents",
 Billings: "/billing",
};

/* =============================
 MODULE PERMISSION MAP
============================= */
const MODULE_PERMISSIONS = {
 DPR: "dprs",
 WIR: "wirs",
 MIR: "mirs",
 "JMR Summary": "jmrs",
 "JMR Sheets": "jmrs",
 "TC/Warranty Card": "tcs",
 Drawing: "drawings",
 "TDS/Manual": "tds",
 PO: "pos",
 // GC is guarded as read:gantt in the backend router
 GC: "gantt",
 BOQA: "workorders",
 HIRA: "hira",
 MOS: "mos",
 // "+ Custom": "documents",
 MOM: "moms",
 WO: "workorders",
 Indent: "indents",
 DC: "deliveries",
 "Contractor DC": "contractor-deliveries",
 "Testing & Commissioning": "tcs",
 "T & C Report": "tcs",
 "Handover Documents": "handovers",
 // This route is guarded as read:contractualwo in the router
 "Contractor WO": "contractualwo",
 "Contractor Billing": "contractualwo-billings",
 "Vendor Billing": "vendor-work-order-billings",
 Reconciliation: "reconciliations",
 Documents: "documents",
 Billings: "billings",
};

const ProjectList = () => {
 const navigate = useNavigate();
 const user = useSelector((state) => state.auth?.userDetails);
 const currentRole = String(user?.role?.name || user?.roleName || user?.role || "").toLowerCase();
 const canAddProject = !["client", "po"].includes(currentRole);

 const [projects, setProjects] = useState([]);
 const [workOrdersByProject, setWorkOrdersByProject] = useState({});
 const [loading, setLoading] = useState(true);
 const [customButtons, setCustomButtons] = useState([]); // dynamic buttons from DB
 const [filters, setFilters] = usePersistentFilters("projects_filters", {
 search: "",
 client: "",
 status: "In Progress",
 billingStatus: "",
 });

 const [currentPage, setCurrentPage] = usePersistentState("projects_page", 1);
 const itemsPerPage = 10;

 const handlePageChange = (pageNumber) => {
 setCurrentPage(pageNumber);
 };

 const [showModal, setShowModal] = useState(false);
 const [selectedProject, setSelectedProject] = useState(null);
 const [selectedModule, setSelectedModule] = useState("");
 const [selectedWO, setSelectedWO] = useState("");
 const [checking, setChecking] = useState(false);
 const [hasRecords, setHasRecords] = useState(false);
 const [billings, setBillings] = useState([]);

 // GitHub-style delete project state
 const [deleteModalOpen, setDeleteModalOpen] = useState(false);
 const [projectToDelete, setProjectToDelete] = useState(null);
 const [depData, setDepData] = useState({ loading: false, total: 0, counts: {} });
 const [typedConfirmName, setTypedConfirmName] = useState("");
 const [isDeleting, setIsDeleting] = useState(false);

 // Active vs Trash toggle
 const [viewMode, setViewMode] = useState("active"); // "active" | "trash"
 const [trashCount, setTrashCount] = useState(0);

 const getProjectStatus = (pid) => {
 const wo = workOrdersByProject[pid] || [];
 if (!wo.length) return "Not Started";
 if (wo.some((w) => w.status === "Delayed")) return "Delayed";
 if (wo.every((w) => w.status === "Completed")) return "Completed";
 if (wo.some((w) => w.status === "In Progress")) return "In Progress";
 return "Pending";
 };

 const getProjectBillingStatus = (project) => {
 const wo = workOrdersByProject[project?._id] || [];
 if (!wo.length) return "No Work Order";

 const totalValue = wo.reduce((sum, w) => sum + Number(w.totalAmount || w.value || 0), 0);
 const woIds = new Set(wo.map((w) => String(w._id)));
 const billed = billings.reduce((sum, b) => {
 const billingWoId = String(typeof b.workOrder === "object" ? b.workOrder?._id : b.workOrder || "");
 if (!woIds.has(billingWoId)) return sum;
 return sum + Number(b.totalAmount || 0);
 }, 0);

 if (billed <= 0) return "Unbilled";
 if (!totalValue) return "Billed";
 if (billed >= totalValue) return "Fully Billed";
 return "Partially Billed";
 };

 const clientOptions = useMemo(() => {
 const map = new Map();
 projects.forEach((project) => {
 const client = project?.client;
 const clientId = client?._id ? String(client._id) : "";
 if (!clientId) return;
 if (!map.has(clientId)) {
 map.set(clientId, {
 value: clientId,
 label: client.companyName || client.name || "Client",
 });
 }
 });
 return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
 }, [projects]);

 const filteredProjects = useMemo(() => {
 const search = String(filters.search || "").trim().toLowerCase();

 return projects.filter((project) => {
 const projectStatus = getProjectStatus(project._id);
 const billingStatus = getProjectBillingStatus(project);
 const clientId = String(project?.client?._id || "");
 const projectName = String(project?.projectName || "").toLowerCase();
 const projectCode = String(project?.projectCode || "").toLowerCase();

 if (filters.client && clientId !== filters.client) return false;
 // "In Progress" filter groups: In Progress, Pending, Delayed, Not Started
 if (filters.status === "Completed" && projectStatus !== "Completed") return false;
 if (filters.status === "In Progress" && projectStatus === "Completed") return false;
 if (filters.billingStatus && billingStatus !== filters.billingStatus) return false;

 if (search && !`${projectName} ${projectCode}`.includes(search)) return false;

 return true;
 });
 }, [projects, filters, workOrdersByProject, billings]);

 const indexOfLastItem = currentPage * itemsPerPage;
 const indexOfFirstItem = indexOfLastItem - itemsPerPage;
 const currentItems = filteredProjects.slice(indexOfFirstItem, indexOfLastItem);
 const totalPages = Math.ceil(filteredProjects.length / itemsPerPage);

 useEffect(() => {
 setCurrentPage(1);
 }, [filters.search, filters.client, filters.status, filters.billingStatus]);

 /* =============================
 LOAD PROJECTS + WORK ORDERS
 ============================= */
  const fetchTrashCount = async () => {
    try {
      const res = await api.get("/projects?isDeleted=true&limit=1");
      setTrashCount(res.data?.pagination?.totalCount || (Array.isArray(res.data?.data) ? res.data.data.length : 0));
    } catch {
      setTrashCount(0);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);

        const projectUrl =
          viewMode === "trash"
            ? "/projects?isDeleted=true&limit=1000"
            : "/projects?limit=1000";

        // 1. Load Projects + custom buttons in parallel
        const [pRes, menuRes] = await Promise.all([
          api.get(projectUrl),
          api.get("/project-menu-buttons").catch(() => ({ data: { data: [] } })),
        ]);
        const list = pRes.data.data || [];
        setProjects(list);
        setCustomButtons(menuRes.data.data || []);
        fetchTrashCount();

        // 2. Load Billing ONLY if user has permission
        const normalize = (str) => (str || "").toLowerCase().replace(/[:\s_-]/g, "");
        const userPerms = (user?.permissionNames || []).map((p) => normalize(p));
        const hasBillingPerm =
          userPerms.includes("readbillings") ||
          userPerms.includes("viewbillings") ||
          userPerms.includes("billing") ||
          (user?.role?.name || "").toLowerCase() === "admin";

        if (hasBillingPerm) {
          try {
            const billingRes = await api.get("/billing");
            setBillings(billingRes.data.data || []);
          } catch (err) {
            console.error("Billing fetch failed:", err);
            setBillings([]);
          }
        } else {
          setBillings([]);
        }

        // 3. Load Work Orders for each project
        const map = {};
        await Promise.all(
          list.map(async (p) => {
            try {
              const woRes = await api.get(`/work-orders?project=${p._id}&includeDeleted=true`);
              map[p._id] = woRes.data.data || [];
            } catch (err) {
              console.error(`WO fetch failed for project ${p._id}:`, err);
              map[p._id] = [];
            }
          })
        );
        setWorkOrdersByProject(map);
      } catch (err) {
        console.error("Project list load failed:", err);
        setProjects([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user, viewMode]);

 /* =============================
 DERIVED PROJECT DATA
 ============================= */
 const getProjectDates = (pid) => {
 const wo = workOrdersByProject[pid] || [];
 if (!wo.length) return "-";
 const start = new Date(Math.min(...wo.map((w) => new Date(w.startDate))));
 const end = new Date(Math.max(...wo.map((w) => new Date(w.endDate))));
 return `${start.toLocaleDateString()} – ${end.toLocaleDateString()}`;
 };

 const getCompletion = (pid) => {
 const wo = workOrdersByProject[pid] || [];
 if (!wo.length) return 0;

 // total project value
 const totalValue = wo.reduce(
 (sum, w) => sum + Number(w.totalAmount || w.value || 0),
 0
 );

 let billed = 0;

 billings.forEach((b) => {
 const woId =
 typeof b.workOrder === "object" ? b.workOrder._id : b.workOrder;

 const belongsToProject = wo.find((w) => w._id === woId);

 if (!belongsToProject) return;

 billed += Number(b.totalAmount || 0);
 });

 if (!totalValue) return 0;

 const percent = (billed / totalValue) * 100;

 return Math.min(100, Math.round(percent));
 };

 const statusVariant = (s) =>
 s === "Completed"
 ? "success"
 : s === "In Progress"
 ? "info"
 : s === "Delayed"
 ? "danger"
 : "secondary";

 const getClientLogoUrl = (project) => {
 const client = project?.client || {};
 return getFileUrl(
 client.image ||
 client.logo ||
 client.brandLogo ||
 client.avatar ||
 client.profileImage ||
 client.photo ||
 ""
 );
 };

 const handleFilterChange = (e) => {
 const { name, value } = e.target;
 setFilters((prev) => ({ ...prev, [name]: value }));
 };

 const clearFilters = () => {
    setFilters({
      search: "",
      client: "",
      status: viewMode === "trash" ? "" : "In Progress",
      billingStatus: "",
    });
  };

 /* =============================
 SAFE MODULE HANDLER
 ============================= */
 const handleModuleClick = async (project, module) => {
 // frontend navigation check
 if (!ROUTE_LOOKUP[module]) return;

 if (module === "Vendor Billing") {
 setSelectedProject(project);
 setSelectedModule(module);
 setShowModal(true);
 setChecking(true);
 setHasRecords(false);

 try {
 const woList = workOrdersByProject[project._id] || [];
 if (!woList.length) {
 setChecking(false);
 return;
 }

 setSelectedWO(woList[0]._id);

 for (const wo of woList) {
 try {
 const billingRes = await api.get(`/vendor-work-order-billings?workOrder=${wo._id}&limit=1`);
 if (billingRes.data?.data?.length) {
 setHasRecords(true);
 setSelectedWO(wo._id);
 break;
 }
 } catch (err) {
 if (err.response?.status !== 404) {
 console.error("Vendor billing check failed:", err);
 }
 }
 }
 } catch (err) {
 console.error("Vendor work order load failed:", err);
 } finally {
 setChecking(false);
 }
 return;
 }

 const apiPath = module === "GC" ? API_LOOKUP.GC : API_LOOKUP[module];

 if (!apiPath) return;

 setSelectedProject(project);
 setSelectedModule(module);
 setShowModal(true);
 setChecking(true);
 setHasRecords(false);

 const woList = workOrdersByProject[project._id] || [];
 if (!woList.length) {
 setChecking(false);
 return;
 }

 setSelectedWO(woList[0]._id);

 try {
 for (const wo of woList) {
 try {
 const res = await api.get(
 typeof apiPath === "function"
 ? apiPath(wo._id)
 : `${apiPath}?workOrder=${wo._id}&limit=1`,
 );

 if (res.data?.data?.length) {
 setHasRecords(true);
 setSelectedWO(wo._id);
 break;
 }
 } catch (err) {
 // 404 = no records yet (safe to ignore)
 if (err.response?.status !== 404) {
 console.error("Module check failed:", err);
 }
 }
 }
 } finally {
 setChecking(false);
 }
 };

  /* =============================
     DELETE PROJECT (GITHUB-STYLE)
  ============================= */
  const handleDeleteClick = async (project) => {
    setProjectToDelete(project);
    setTypedConfirmName("");
    setDeleteModalOpen(true);
    setDepData({ loading: true, total: 0, counts: {} });

    try {
      const res = await api.get(`/projects/${project._id}/dependencies`);
      if (res.data?.success) {
        setDepData({
          loading: false,
          total: res.data.totalDependencies || 0,
          counts: res.data.counts || {},
        });
      } else {
        setDepData({ loading: false, total: 0, counts: {} });
      }
    } catch (err) {
      console.error("Failed to fetch project dependencies:", err);
      setDepData({ loading: false, total: 0, counts: {} });
    }
  };

  const handleConfirmDelete = async () => {
    if (!projectToDelete) return;
    if (
      typedConfirmName.trim().toLowerCase() !==
      projectToDelete.projectName.trim().toLowerCase()
    ) {
      swal("Mismatch", "Project name does not match.", "error");
      return;
    }

    try {
      setIsDeleting(true);
      const res = await api.delete(`/projects/${projectToDelete._id}`, {
        data: { confirmProjectName: typedConfirmName.trim() },
        params: { confirmProjectName: typedConfirmName.trim() },
      });

      if (res.data?.success) {
        setProjects((prev) => prev.filter((p) => p._id !== projectToDelete._id));
        setDeleteModalOpen(false);
        setProjectToDelete(null);
        setTypedConfirmName("");
        swal("Deleted!", res.data.message || "Project deleted successfully", "success");
      } else {
        swal("Error", res.data?.message || "Failed to delete project", "error");
      }
    } catch (err) {
      console.error("Delete project error:", err);
      swal(
        "Error",
        err.response?.data?.message || "Failed to delete project",
        "error"
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRestoreClick = async (p) => {
    const confirm = await swal({
      title: "Restore Project?",
      text: `Are you sure you want to restore "${p.projectName}"? This project and all its associated work orders, purchase orders, and records will be restored immediately.`,
      icon: "info",
      buttons: ["Cancel", "Yes, Restore Project"],
    });
    if (!confirm) return;

    try {
      const res = await api.patch(`/projects/${p._id}/restore`);
      if (res.data?.success) {
        setProjects((prev) => prev.filter((item) => item._id !== p._id));
        setTrashCount((prev) => Math.max(0, prev - 1));
        swal("Restored!", res.data.message || `Project "${p.projectName}" restored successfully`, "success");
      } else {
        swal("Error", res.data?.message || "Failed to restore project", "error");
      }
    } catch (err) {
      swal("Error", err.response?.data?.message || "Failed to restore project", "error");
    }
  };

  return (
    <>
      <PageTitle activeMenu="Projects" motherMenu="Master" />

      <Row className="mb-3 align-items-center">
        <Col xs={12} md={7} className="d-flex align-items-center flex-wrap gap-4 mb-2 mb-md-0">
          <h4 className="fw-bold mb-0">Projects</h4>
          <div
            className="d-inline-flex align-items-center bg-white border rounded-3 p-1 shadow-sm"
            style={{ height: "40px", boxSizing: "border-box" }}
          >
            <button
              type="button"
              className={`btn d-inline-flex align-items-center justify-content-center px-3 border-0 ${
                viewMode === "active"
                  ? "btn-primary text-white shadow-sm"
                  : "btn-light text-muted bg-transparent"
              }`}
              style={{
                height: "32px",
                fontSize: "0.85rem",
                fontWeight: 500,
                borderRadius: "6px",
                gap: "8px",
                lineHeight: 1,
                transition: "all 0.2s ease",
              }}
              onClick={() => {
                setViewMode("active");
                setCurrentPage(1);
                setFilters({ search: "", client: "", status: "In Progress", billingStatus: "" });
              }}
            >
              <i className="fas fa-folder-open" style={{ fontSize: "0.85rem" }}></i>
              <span>Active Projects</span>
            </button>
            <button
              type="button"
              className={`btn d-inline-flex align-items-center justify-content-center px-3 border-0 ms-1 ${
                viewMode === "trash"
                  ? "btn-danger text-white shadow-sm"
                  : "btn-light text-muted bg-transparent"
              }`}
              style={{
                height: "32px",
                fontSize: "0.85rem",
                fontWeight: 500,
                borderRadius: "6px",
                gap: "8px",
                lineHeight: 1,
                transition: "all 0.2s ease",
              }}
              onClick={() => {
                setViewMode("trash");
                setCurrentPage(1);
                setFilters({ search: "", client: "", status: "", billingStatus: "" });
              }}
            >
              <i
                className="fas fa-trash-alt"
                style={{
                  fontSize: "0.85rem",
                  color: viewMode === "trash" ? "#fff" : "#dc3545",
                }}
              ></i>
              <span style={{ color: viewMode === "trash" ? "#fff" : "#dc3545" }}>
                Trash / Archived
              </span>
              {trashCount > 0 && (
                <span
                  style={{
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    padding: "2px 7px",
                    borderRadius: "10px",
                    backgroundColor:
                      viewMode === "trash" ? "rgba(255,255,255,0.9)" : "#fee2e2",
                    color: viewMode === "trash" ? "#dc3545" : "#ef4444",
                    lineHeight: 1.2,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {trashCount}
                </span>
              )}
            </button>
          </div>
        </Col>
        <Col xs={12} md={5} className="text-md-end">
          {canAddProject && viewMode === "active" && (
            <Button
              variant="primary"
              className="d-inline-flex align-items-center justify-content-center px-3 shadow-sm border-0"
              style={{
                height: "40px",
                fontSize: "0.85rem",
                fontWeight: 500,
                gap: "6px",
                borderRadius: "8px",
              }}
              onClick={() => navigate("/projects/add")}
            >
              <i className="fas fa-plus"></i>
              <span>New Project</span>
            </Button>
          )}
        </Col>
      </Row>

 <Card className="shadow-sm border-0 mb-3">
 <Card.Body>
 <Row className="g-2">
 <Col xl={4} lg={6} md={6}>
 <Form.Control
 name="search"
 value={filters.search}
 onChange={handleFilterChange}
 placeholder="Search project name or code"
 />
 </Col>
 <Col xl={3} lg={6} md={6}>
 <Form.Select name="client" value={filters.client} onChange={handleFilterChange}>
 <option value="">All Clients</option>
 {clientOptions.map((client) => (
 <option key={client.value} value={client.value}>
 {client.label}
 </option>
 ))}
 </Form.Select>
 </Col>
 <Col xl={2} lg={6} md={6}>
 <Form.Select name="status" value={filters.status} onChange={handleFilterChange}>
 <option value="">All Status</option>
 <option value="In Progress">In Progress</option>
 <option value="Completed">Completed</option>
 </Form.Select>
 </Col>
 <Col xl={2} lg={6} md={6}>
 <Form.Select
 name="billingStatus"
 value={filters.billingStatus}
 onChange={handleFilterChange}
 >
 <option value="">Billing Status</option>
 <option value="No Work Order">No Work Order</option>
 <option value="Unbilled">Unbilled</option>
 <option value="Partially Billed">Partially Billed</option>
 <option value="Fully Billed">Fully Billed</option>
 <option value="Billed">Billed</option>
 </Form.Select>
 </Col>
 <Col xl={1} lg={12} md={12} className="d-grid">
 <Button variant="outline-secondary" onClick={clearFilters}>
 Reset
 </Button>
 </Col>
 </Row>
 </Card.Body>
 </Card>

 {loading ? (
 <div className="text-center my-5">
 <Spinner />
 </div>
 ) : filteredProjects.length === 0 ? (
 <Card className="border-0 shadow-sm text-center py-5 my-3">
 <Card.Body>
 <div
 className="d-inline-flex align-items-center justify-content-center mb-3 rounded-circle"
 style={{
 width: "72px",
 height: "72px",
 backgroundColor: viewMode === "trash" ? "#fee2e2" : "#f1f5f9",
 color: viewMode === "trash" ? "#dc3545" : "#64748b",
 fontSize: "28px",
 }}
 >
 <i className={viewMode === "trash" ? "fas fa-trash-alt" : "fas fa-folder-open"}></i>
 </div>
 <h5 className="fw-bold mb-1" style={{ color: "#1e293b" }}>
 {viewMode === "trash" ? "No Projects in Trash" : "No Projects Found"}
 </h5>
 <p className="text-muted mb-0" style={{ fontSize: "0.9rem" }}>
 {viewMode === "trash"
 ? "There are currently no deleted or archived projects in the trash."
 : "Try adjusting your search or filter criteria to find what you're looking for."}
 </p>
 {viewMode !== "trash" && canAddProject && (
 <Button
 variant="primary"
 size="sm"
 className="mt-3 px-3 shadow-sm border-0"
 onClick={() => navigate("/projects/add")}
 >
 <i className="fas fa-plus me-1"></i> Create New Project
 </Button>
 )}
 </Card.Body>
 </Card>
 ) : (
 <>
 <Row className="g-3">
 {currentItems.map((p) => {
 const status = getProjectStatus(p._id);
 const billingStatus = getProjectBillingStatus(p);
 const percent = getCompletion(p._id);
 return (
 <Col key={p._id} xs={12} md={6} xl={4}>
 <Card className="h-100 shadow-sm">
 <Card.Body style={{ position: "relative", paddingTop: "2.75rem" }}>
 {viewMode !== "trash" && (
    <div style={{ position: "absolute", top: "0.35rem", right: "0.6rem", zIndex: 2 }}>
      <Dropdown align="end">
        <Dropdown.Toggle size="sm" className="i-false">
          <i className="fas fa-ellipsis-v"></i>
        </Dropdown.Toggle>
        <Dropdown.Menu>
          <CanAccess allowedRoles={["Admin", "Manager"]}>
            <Dropdown.Item
              onClick={() =>
                navigate(`/projects/edit/${p._id}`)
              }
            >
              <i className="fas fa-edit me-2"></i>Edit
            </Dropdown.Item>

            <Dropdown.Item
              onClick={() =>
                navigate(`/delivery?project=${p._id}`)
              }
            >
              <i className="fas fa-truck me-2"></i>Deliveries
            </Dropdown.Item>

            <Dropdown.Item
              onClick={() =>
                navigate(`/documents?project=${p._id}`)
              }
            >
              <i className="fas fa-folder-open me-2"></i>Documents
            </Dropdown.Item>

            <Dropdown.Item
              onClick={() =>
                navigate(
                  `/billings?workOrder=${workOrdersByProject[p._id]?.[0]?._id || ""}&project=${p._id}`
                )
              }
            >
              <i className="fas fa-file-invoice-dollar me-2"></i>Billings
            </Dropdown.Item>
          </CanAccess>

          <CanAccess allowedRoles={["Admin"]}>
            <>
              <Dropdown.Divider />
              <Dropdown.Item
                className="text-danger"
                onClick={() => handleDeleteClick(p)}
              >
                <i className="fas fa-trash me-2"></i>Delete
              </Dropdown.Item>
            </>
          </CanAccess>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  )}
 {/* HEADER */}
 <div className="d-flex justify-content-between align-items-start gap-1">
 <div style={{ minWidth: 0, flex: 1 }}>
 <h6 className="fw-bold mb-1" style={{ lineHeight: 1.2 }}>
 {p.projectCode} - {p.projectName}
 </h6>
 <div className="text-muted small">
 {p.client?.companyName || p.client?.name || "Client"}
 </div>
 </div>
 <div className="d-flex flex-column align-items-end gap-1">
 <div
 title={p.client?.companyName || p.client?.name || "Client"}
 style={{
 width: 70,
 height: 70,
 border: "1px solid #e5e8ef",
 borderRadius: 14,
 overflow: "hidden",
 display: "flex",
 alignItems: "center",
 justifyContent: "center",
 background: "#fff",
 flexShrink: 0,
 marginRight: "25px",
 marginTop: "0.45rem",
 }}
 >
 {getClientLogoUrl(p) ? (
 <img
 src={getClientLogoUrl(p)}
 alt={p.client?.companyName || p.client?.name || "Client Logo"}
 style={{
 width: "100%",
 height: "100%",
 objectFit: "contain",
 padding: 8,
 }}
 />
 ) : (
 <div
 className="text-center text-muted fw-bold"
 style={{ fontSize: 11, padding: 8, lineHeight: 1.1 }}
 >
 {(p.client?.companyName || p.client?.name || "Client")
 .split(" ")
 .slice(0, 2)
 .map((part) => part?.[0]?.toUpperCase() || "")
 .join("") || "CL"}
 </div>
 )}
 </div>
 </div>
 </div> <Badge bg="primary" className="mt-1 rounded-pill">
 {getProjectDates(p._id)}
 </Badge>

 <br />

 <Badge bg={statusVariant(status)} className="mt-1 rounded-pill">
 {status}
 </Badge>
 <Badge
 bg={
 billingStatus === "Fully Billed"
 ? "success"
 : billingStatus === "Partially Billed"
 ? "warning"
 : billingStatus === "Billed"
 ? "info"
 : "secondary"
 }
 className="mt-1 ms-2 rounded-pill"
 >
 {billingStatus}
 </Badge>



 <div className="d-flex justify-content-center mt-3">
 <div
 className="circular-progress"
 style={{
 "--value": percent,
 "--progress-color":
 percent === 100
 ? "#198754"
 : percent > 0
 ? "#ffc107"
 : "#dc3545",
 }}
 >
 <span>{percent}%</span>
 </div>
 </div>
  {viewMode === "trash" ? (
    <div className="p-3 bg-light rounded text-center mt-3 border">
      <div className="text-danger fw-bold small mb-1">
        <i className="fas fa-trash-alt me-1"></i> In Trash / Soft-Deleted
      </div>
      <div className="text-muted small mb-3">
        This project is soft-deleted. Click Restore to reactivate it and all its linked work orders and documentation.
      </div>
      <Button
        variant="success"
        size="sm"
        className="w-100 fw-bold shadow-sm"
        onClick={() => handleRestoreClick(p)}
      >
        <i className="fas fa-undo me-2"></i> Restore Project
      </Button>
    </div>
  ) : (
    <>
      <div className="d-flex gap-2 mt-3">
        <CanAccess permission="read:mcs">
          <Button
            size="sm"
            variant="outline-primary"
            className="w-100"
            onClick={() => navigate(`/projects/${p._id}/mcs`)}
          >
            <i className="fas fa-chart-gantt me-2"></i>MCS
          </Button>
        </CanAccess>
      </div>

      {/* [OK] SECTIONS w/ NEW MODULE SUPPORT */}
      <Section
        title="Reports & Drawings"
        items={[
          "DPR",
          "WIR",
          "MIR",
          "JMR Summary",
          "Drawing",
          "TDS/Manual",
          "TC/Warranty Card",
          "JMR Sheets",
          "Documents",
        ]}
        customButtons={customButtons.filter(b => b.section === "Reports & Drawings")}
        project={p}
        workOrders={workOrdersByProject[p._id] || []}
        onClick={(m) => handleModuleClick(p, m)}
      />
      <Section
        title="Planning & Control"
        items={["GC", "BOQA"]}
        customButtons={customButtons.filter(b => b.section === "Planning & Control")}
        project={p}
        workOrders={workOrdersByProject[p._id] || []}
        onClick={(m) => handleModuleClick(p, m)}
      />
      <Section
        title="Safety & Docs"
        items={["HIRA", "MOS"]}
        customButtons={customButtons.filter(b => b.section === "Safety & Docs")}
        project={p}
        workOrders={workOrdersByProject[p._id] || []}
        onClick={(m) => handleModuleClick(p, m)}
      />
      <Section
        title="Execution"
        items={["MOM", "WO", "Indent", "DC", "Contractor DC"]}
        customButtons={customButtons.filter(b => b.section === "Execution")}
        project={p}
        workOrders={workOrdersByProject[p._id] || []}
        onClick={(m) => handleModuleClick(p, m)}
      />
      <Section
        title="Quality & Closure"
        items={[
          "T & C Report",
          "Handover Documents",
        ]}
        customButtons={customButtons.filter(b => b.section === "Quality & Closure")}
        project={p}
        workOrders={workOrdersByProject[p._id] || []}
        onClick={(m) => handleModuleClick(p, m)}
      />
      <Section
        title="Commercial"
        highlight
        items={["Contractor WO", "Contractor Billing", "Vendor Billing", "Reconciliation", "Billings"]}
        customButtons={customButtons.filter(b => b.section === "Commercial")}
        project={p}
        workOrders={workOrdersByProject[p._id] || []}
        onClick={(m) => handleModuleClick(p, m)}
      />
      {/* Dynamic custom sections (not in default 6) */}
      {[...new Set(customButtons.map(b => b.section))]
        .filter(s => !["Reports & Drawings","Planning & Control","Safety & Docs","Execution","Quality & Closure","Commercial"].includes(s))
        .map(s => (
          <Section
            key={s}
            title={s}
            items={[]}
            customButtons={customButtons.filter(b => b.section === s)}
            project={p}
            workOrders={workOrdersByProject[p._id] || []}
            onClick={(m) => handleModuleClick(p, m)}
          />
        ))
      }
    </>
  )}
 </Card.Body>
 </Card>
 </Col>
 );
 })}
 </Row>

 {/* PAGINATION */}
 {filteredProjects.length > itemsPerPage && (
 <AppPagination
 currentPage={currentPage}
 totalPages={totalPages}
 totalCount={filteredProjects.length}
 itemsLength={10}
 limit={itemsPerPage}
 onPageChange={handlePageChange}
 />
 )}
 </>
 )}

 {/* MODAL */}
 <Modal show={showModal} onHide={() => setShowModal(false)} centered>
 <Modal.Header closeButton>
 <Modal.Title>{selectedModule}</Modal.Title>
 </Modal.Header>
 <Modal.Body>
 {checking ? (
 "Checking records…"
 ) : (
 <>
 <Form.Select
 value={selectedWO}
 onChange={(e) => setSelectedWO(e.target.value)}
 className="mb-3"
 >
 {(
 selectedModule === "Vendor Billing"
 ? workOrdersByProject[selectedProject?._id] || []
 : workOrdersByProject[selectedProject?._id] || []
 ).map((wo) => (
 <option key={wo._id} value={wo._id}>
 {wo.woNo || wo.woNumber || wo.title || "WO"}{wo.label ? ` – ${wo.label}` : ""}
 </option>
 ))}
 </Form.Select>

 <Button
 className="w-100"


 onClick={() => {
 let route = ROUTE_LOOKUP[selectedModule];

 // =====================
 // GC → Gantt
 // =====================
 if (selectedModule === "GC") {
 route = route.replace(":id", selectedWO);
 navigate(`/${route}`);
 return;
 }

 // =====================
 // WORK ORDERS LIST
 // =====================
 if (selectedModule === "WO") {
 navigate(`/work-orders?project=${selectedProject._id}`);
 return;
 }

 // =====================
 // Vendor Billing
 // =====================
 if (selectedModule === "Vendor Billing") {
 navigate(
 hasRecords
 ? `/vendor-work-order-billing?workOrder=${selectedWO}&project=${selectedProject._id}`
 : `/vendor-work-order-billing/add?workOrder=${selectedWO}&project=${selectedProject._id}`
 );
 return;
 }

 // =====================
 // Drawing / HIRA / MOS / Custom → Documents
 // =====================
 if (
 selectedModule === "Drawing" ||
 selectedModule === "HIRA" ||
 selectedModule === "MOS"
 ) {
 navigate(
 `/documents?project=${selectedProject._id}&workOrder=${selectedWO}&documentType=${selectedModule}`
 );
 return;
 }

 // =====================
 // DEFAULT MODULES
 // =====================
 if (selectedModule === "Billings") {
 navigate(
 hasRecords
 ? `/billings?workOrder=${selectedWO}&project=${selectedProject._id}`
 : `/billings/add?workOrder=${selectedWO}&project=${selectedProject._id}`
 );
 return;
 }

 navigate(
 `/${route}?project=${selectedProject._id}&workOrder=${selectedWO}`
 );
 }}
 >
 {hasRecords ? "View" : "Create"} {selectedModule}
 </Button>
 </>
 )}
 </Modal.Body>
 </Modal>

  {/* ======================================================
      DELETE PROJECT MODAL (STANDARD THEME STYLE)
  ====================================================== */}
  <Modal
    show={deleteModalOpen}
    onHide={() => !isDeleting && setDeleteModalOpen(false)}
    centered
  >
    <Modal.Header closeButton>
      <Modal.Title>Delete Project</Modal.Title>
    </Modal.Header>

    <Modal.Body>
      <p className="mb-1">
        Are you sure you want to delete project{" "}
        <b>{projectToDelete?.projectName}</b>?
      </p>
      {projectToDelete?.projectCode && (
        <p className="text-muted mb-2">
          <small>Project Code: {projectToDelete.projectCode}</small>
        </p>
      )}

      {/* Dependency Check Result */}
      {depData.loading ? (
        <div className="text-muted py-2">
          <Spinner animation="border" size="sm" className="me-2" />
          <small>Checking linked records...</small>
        </div>
      ) : depData.total > 0 ? (
        <div className="alert alert-danger py-2 px-3 mt-2 mb-3">
          <div className="fw-bold mb-1">
            <i className="fas fa-exclamation-triangle me-2"></i>
            Warning: This project has {depData.total} linked records:
          </div>
          <div className="d-flex flex-wrap gap-1 mt-1">
            {depData.counts?.workOrders > 0 && (
              <Badge bg="danger" className="me-1">
                {depData.counts.workOrders} Work Orders
              </Badge>
            )}
            {depData.counts?.vendorWorkOrders > 0 && (
              <Badge bg="danger" className="me-1">
                {depData.counts.vendorWorkOrders} Vendor WOs
              </Badge>
            )}
            {depData.counts?.purchaseOrders > 0 && (
              <Badge bg="danger" className="me-1">
                {depData.counts.purchaseOrders} Purchase Orders
              </Badge>
            )}
            {depData.counts?.bills > 0 && (
              <Badge bg="danger" className="me-1">
                {depData.counts.bills} Invoices
              </Badge>
            )}
            {depData.counts?.tasks > 0 && (
              <Badge bg="danger" className="me-1">
                {depData.counts.tasks} Tasks
              </Badge>
            )}
            {depData.counts?.milestones > 0 && (
              <Badge bg="danger" className="me-1">
                {depData.counts.milestones} Milestones
              </Badge>
            )}
            {depData.counts?.dprs > 0 && (
              <Badge bg="danger" className="me-1">
                {depData.counts.dprs} DPRs
              </Badge>
            )}
            {depData.counts?.jmrs > 0 && (
              <Badge bg="danger" className="me-1">
                {depData.counts.jmrs} JMRs
              </Badge>
            )}
            {depData.counts?.measurementSheets > 0 && (
              <Badge bg="danger" className="me-1">
                {depData.counts.measurementSheets} Measurement Sheets
              </Badge>
            )}
            {depData.counts?.wirs > 0 && (
              <Badge bg="danger" className="me-1">
                {depData.counts.wirs} WIRs
              </Badge>
            )}
            {depData.counts?.mirs > 0 && (
              <Badge bg="danger" className="me-1">
                {depData.counts.mirs} MIRs
              </Badge>
            )}
            {depData.counts?.qcs > 0 && (
              <Badge bg="danger" className="me-1">
                {depData.counts.qcs} QC Inspections
              </Badge>
            )}
            {depData.counts?.safetyIncidents > 0 && (
              <Badge bg="danger" className="me-1">
                {depData.counts.safetyIncidents} Safety Incidents
              </Badge>
            )}
            {depData.counts?.indents > 0 && (
              <Badge bg="danger" className="me-1">
                {depData.counts.indents} Indents
              </Badge>
            )}
            {depData.counts?.clientTickets > 0 && (
              <Badge bg="danger" className="me-1">
                {depData.counts.clientTickets} Tickets
              </Badge>
            )}
          </div>
          <p className="text-danger mb-0 mt-2" style={{ fontSize: "12px" }}>
            Deleting will permanently remove the project and all these linked records.
          </p>
        </div>
      ) : (
        <p className="text-muted mt-2 mb-3">
          <small>This project has no linked records.</small>
        </p>
      )}

      {/* GitHub-style confirmation input */}
      <div className="mt-3">
        <Form.Group>
          <Form.Label className="mb-1">
            To confirm, please type <strong>{projectToDelete?.projectName}</strong> in the box below:
          </Form.Label>
          <Form.Control
            type="text"
            value={typedConfirmName}
            onChange={(e) => setTypedConfirmName(e.target.value)}
            placeholder={`Enter "${projectToDelete?.projectName}"`}
            disabled={isDeleting}
            autoFocus
          />
        </Form.Group>
      </div>
    </Modal.Body>

    <Modal.Footer>
      <Button
        variant="secondary"
        onClick={() => setDeleteModalOpen(false)}
        disabled={isDeleting}
      >
        Cancel
      </Button>
      <Button
        variant="danger"
        disabled={
          typedConfirmName.trim().toLowerCase() !==
            projectToDelete?.projectName?.trim().toLowerCase() ||
          isDeleting ||
          depData.loading
        }
        onClick={handleConfirmDelete}
      >
        {isDeleting ? "Deleting..." : "Delete Project"}
      </Button>
    </Modal.Footer>
  </Modal>


 </>
 );
};

/* =============================
 SECTION COMPONENT
============================= */
const Section = ({ title, items, highlight, onClick, customButtons = [], project, workOrders = [] }) => {
 const navigate = useNavigate();
 const user = useSelector((state) => state.auth?.userDetails);

 // Check which static items are visible for this user
 const visibleItems = items.filter((item) => {
 const routeEnabled = Boolean(ROUTE_LOOKUP[item]);
 const permissionKey = MODULE_PERMISSIONS[item];
 if (!routeEnabled || !permissionKey) return false;

 let roleName = "";
 if (typeof user?.role === "string") roleName = user.role;
 else if (typeof user?.role === "object") roleName = user?.role?.name || "";
 if (roleName.toLowerCase() === "admin") return true;

   const normalize = (str) => (str || "").toLowerCase().replace(/[:\s_-]/g, "");
  const userPerms = (user?.permissionNames || []).map((p) => normalize(p));
  const rawKey = normalize(permissionKey);
  const reqPerm = item === "WO" ? "readwo" : normalize(`read:${permissionKey}`);

  return (
    userPerms.includes(reqPerm) ||
    userPerms.includes(`read${rawKey}`) ||
    userPerms.includes(`view${rawKey}`) ||
    userPerms.includes(rawKey)
  );
 });

 // Check which custom buttons are visible
 const visibleCustom = customButtons.filter((btn) => {
 if (!btn.permission) return true; // no permission required

 let roleName = "";
 if (typeof user?.role === "string") roleName = user.role;
 else if (typeof user?.role === "object") roleName = user?.role?.name || "";
 if (roleName.toLowerCase() === "admin") return true;

 const normalize = (str) => (str || "").toLowerCase().replace(/[:\s_-]/g, "");
 const userPerms = (user?.permissionNames || []).map((p) => normalize(p));
 return userPerms.includes(normalize(btn.permission));
 });

 if (visibleItems.length === 0 && visibleCustom.length === 0) return null;

 const handleCustomClick = (btn) => {
 const firstWo = workOrders?.[0];
 let url = btn.navigateTo
 .replace("{projectId}", project?._id || "")
 .replace("{workOrderId}", firstWo?._id || "");
 navigate(url);
 };

 return (
 <div className="mt-3">
 <div className="fw-semibold">{title}</div>
 <div className="d-flex flex-wrap gap-2 mt-1">
 {/* Static buttons */}
 {items.map((item) => {
 const routeEnabled = Boolean(ROUTE_LOOKUP[item]);
 const permissionKey = MODULE_PERMISSIONS[item];
 if (!routeEnabled || !permissionKey) return null;

 // WO button requires BOTH read:workorders AND read:wo
 const multiPerms = item === "WO" ? ["read:wo"] : null;
 const permission = `read:${permissionKey}`;

 return (
 <CanAccess
 key={item}
 permissions={multiPerms || []}
 permission={multiPerms ? "" : permission}
 fallback={null}
 >
 <Button
 size="sm"
 variant={highlight ? "warning" : "outline-primary"}
 onClick={() => onClick(item)}
 >
 {item}
 </Button>
 </CanAccess>
 );
 })}

 {/* Dynamic custom buttons */}
 {visibleCustom.map((btn) => (
 <Button
 key={btn._id}
 size="sm"
 variant={btn.isHighlight ? "warning" : highlight ? "warning" : "outline-success"}
 onClick={() => handleCustomClick(btn)}
 title={btn.navigateTo}
 >
 {btn.label}
 </Button>
 ))}
 </div>
 </div>
 );
};

export default ProjectList;







